// See @store/AppPhaseStore.ts for the store loading order.
// Fetches and holds the grid resources for the current session.
// Call fetchResources() after authentication succeeds. Reads grid ID and observation params
// from EnvStore, fetches the grid visualizer data from the API (or local sample files),
// and hydrates persisted UI state into localStorage.

import { create } from 'zustand';
import type { GridResources, UUID } from '@visualizer/types';
import { NotificationType } from '@visualizer/types';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { DEFAULT_PERSIST_STORAGE_NAME, loadPersistedStateIntoLocalStorage } from '@gui/store/persistent/loadPersistedState';
import useNotificationStore from '@gui/store/notification';
import { getEnvStore } from './EnvStore';
import { ApiRoutes } from './ApiRoutes';

class ResourceError extends Error {
    public readonly statusCode: number;

    constructor(message: string, statusCode: number) {
        super(message);
        this.name = 'ResourceError';
        this.statusCode = statusCode;
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ResourceError);
        }
    }
}

export type ResourceStatus = 'idle' | 'loaded' | 'error';

type ResourceStore = {
    status: ResourceStatus;
    gridId: UUID;
    demoSceneName: string | null;
    projectId: string;
    gridResources: GridResources | null;
    error: string | null;
    errorCode: number | null;
};

type ResourceActions = {
    fetchResources: () => Promise<void>;
};

type VisualizerGridResources = Omit<GridResources, 'project_id' | 'created_at' | 'sensor_points' | 'params' | 'type'> &
    Partial<Pick<GridResources, 'project_id' | 'created_at' | 'sensor_points' | 'params' | 'type'>>;

type GridDetailResponse = {
    projectId?: string;
    createdAt?: string | null;
    params?: {
        gridHeight?: number | null;
        gridResolution?: number | null;
        coordinateReferenceSystem?: {
            point: { x: number; y: number; z: number };
            rotation: number;
        } | null;
    } | null;
};

type ObservationVisualizerResponse = {
    projectId: string;
    grid: VisualizerGridResources;
};

type DemoResourcesResult = {
    sceneName: string;
    resources: GridResources;
};

const mapGridParams = (gridDetails: GridDetailResponse): GridResources['params'] => {
    const coordinateReferenceSystem = gridDetails.params?.coordinateReferenceSystem;

    if (!coordinateReferenceSystem) {
        return {
            coordinateReferenceSystem: null,
            gridHeight: gridDetails.params?.gridHeight ?? null,
            gridResolution: gridDetails.params?.gridResolution ?? null,
        };
    }

    return {
        coordinateReferenceSystem: {
            origin: {
                x: coordinateReferenceSystem.point.x,
                y: coordinateReferenceSystem.point.y,
                z: coordinateReferenceSystem.point.z,
            },
            rotation: (coordinateReferenceSystem.rotation * Math.PI) / 180,
        },
        gridHeight: gridDetails.params?.gridHeight ?? null,
        gridResolution: gridDetails.params?.gridResolution ?? null,
    };
};

const normalizeVisualizerGeodatas = (geodatas: any[] | undefined, gridId: string): GridResources['geodatas'] =>
    (geodatas ?? []).map((geodata) => ({
        ...geodata,
        grid_id: geodata.grid_id ?? geodata.gridId ?? gridId,
        created_at: geodata.created_at ?? geodata.createdAt ?? '',
        status_message: geodata.status_message ?? geodata.errorMessage ?? geodata.failedOnStep ?? '',
        simulations_sets: (geodata.simulations_sets ?? geodata.simulationSets ?? []).map((simulationSet: any) => ({
            ...simulationSet,
            geodata_id: simulationSet.geodata_id ?? simulationSet.geodataId ?? geodata.id,
            created_at: simulationSet.created_at ?? simulationSet.createdAt ?? '',
            simulations: (simulationSet.simulations ?? []).map((simulation: any) => ({
                ...simulation,
                simulation_id: simulation.simulation_id ?? String(simulation.id),
                type: simulation.type ?? simulation.simType ?? 'UNKNOWN',
                exec_time: simulation.exec_time ?? simulation.execTime,
                status_message: simulation.status_message ?? simulation.errorMessage ?? simulation.failedOnStep,
            })),
        })),
    }));

const completeGridResources = (resources: VisualizerGridResources, gridDetails?: GridDetailResponse): GridResources =>
    ({
        ...resources,
        type: 'grid',
        project_id: gridDetails?.projectId ?? resources.project_id ?? '',
        created_at: gridDetails?.createdAt ?? resources.created_at ?? '',
        sensor_points: resources.sensor_points ?? '',
        params: mapGridParams(gridDetails ?? {}),
        geodatas: normalizeVisualizerGeodatas(resources.geodatas, resources.id),
    }) as GridResources;

const fetchGridDetails = async (gridId: string): Promise<GridDetailResponse> => {
    const response = await fetch(`/api/grids/${gridId}`, { credentials: 'include' });

    if (!response.ok) {
        throw new ResourceError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    return (await response.json()) as GridDetailResponse;
};

const fetchGridResources = async (gridId: string): Promise<GridResources> => {
    if (!gridId) {
        throw new ResourceError('Grid ID is not set. Cannot fetch grid resources.', 400);
    }

    const response = await fetch(`/api/grids/${gridId}/visualizer`, { credentials: 'include' });

    if (!response.ok) {
        let errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        try {
            const errorBody = await response.json();
            const backendErrorMessage =
                typeof errorBody?.msg === 'string' && errorBody.msg.trim()
                    ? errorBody.msg.trim()
                    : typeof errorBody?.message === 'string' && errorBody.message.trim()
                      ? errorBody.message.trim()
                      : null;

            if (response.status === 404 && backendErrorMessage && /^Grid\b.*\bnot found$/i.test(backendErrorMessage)) {
                errorMsg = 'GRID_NOT_FOUND';
            } else if (backendErrorMessage) {
                errorMsg = backendErrorMessage;
            }
        } catch {
            // keep fallback HTTP message
        }
        throw new ResourceError(errorMsg, response.status);
    }

    const rawResources = (await response.json()) as VisualizerGridResources;
    const gridDetails = await fetchGridDetails(gridId);
    return completeGridResources(rawResources, gridDetails);
};

const fetchObservationResources = async (projectId: string, reportId: string, observationId: string): Promise<GridResources> => {
    const response = await fetch(`/api/projects/${projectId}/reports/${reportId}/observations/${observationId}/visualizer`, {
        credentials: 'include',
    });

    if (!response.ok) {
        throw new ResourceError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    const raw = (await response.json()) as ObservationVisualizerResponse;
    const fullGridResources = await fetchGridResources(raw.grid.id);

    return {
        ...fullGridResources,
        project_id: raw.projectId ?? fullGridResources.project_id,
    };
};

const fetchLocalSampleResources = async (gridId: string): Promise<GridResources> => {
    if (!gridId) {
        throw new ResourceError('Grid ID is not set. Cannot fetch local sample resources.', 400);
    }

    const response = await fetch(`/sampleData/${gridId}/visualizer.json`);

    if (!response.ok) {
        throw new ResourceError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    return (await response.json()) as GridResources;
};

const fetchDefaultDemoSceneName = async (): Promise<string> => {
    const response = await fetch('/api/demo', { credentials: 'include' });

    if (!response.ok) {
        throw new ResourceError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    const scenes = (await response.json()) as unknown;
    if (!Array.isArray(scenes) || typeof scenes[0] !== 'string' || !scenes[0]) {
        throw new ResourceError('No demo scenes returned by the backend.', 404);
    }

    return scenes[0];
};

const fetchDemoResources = async (sceneNameFromQuery: string): Promise<DemoResourcesResult> => {
    const sceneName = sceneNameFromQuery || (await fetchDefaultDemoSceneName());

    const response = await fetch(`/api/demo/${sceneName}/visualizer`, { credentials: 'include' });

    if (!response.ok) {
        throw new ResourceError(`HTTP ${response.status}: ${response.statusText}`, response.status);
    }

    const rawResources = (await response.json()) as VisualizerGridResources;
    return {
        sceneName,
        resources: completeGridResources(rawResources),
    };
};

const hydratePersistedState = async (gridId: string, projectId: string, observationId: string | null, reportId: string | null) => {
    if (!ApiRoutes.canUseApi()) {
        localStorage.removeItem(DEFAULT_PERSIST_STORAGE_NAME);
        return;
    }

    const apiClient = axios.create({
        baseURL: '/api/',
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
    });

    if (observationId) {
        try {
            const response = await apiClient.get(`projects/${projectId}/reports/${reportId}/observations/${observationId}/state`);
            loadPersistedStateIntoLocalStorage(response.data);
        } catch (error) {
            useNotificationStore
                .getState()
                .showNotification('Could not restore observation state from server. Using existing local state.', NotificationType.Warning);
            console.error(error);
        }
    } else {
        if (!gridId) return;

        try {
            const response = await apiClient.get(`grids/${gridId}/states/automatic/state`);
            loadPersistedStateIntoLocalStorage(response.data);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 404) {
                localStorage.removeItem(DEFAULT_PERSIST_STORAGE_NAME);
                return;
            }

            useNotificationStore
                .getState()
                .showNotification('Could not restore automatic state from server. Using existing local state.', NotificationType.Warning);
            console.warn('Could not load automatic state', error);
        }
    }
};

export const useResourceStore = create<ResourceStore & ResourceActions>((set) => ({
    status: 'idle',
    gridId: '' as UUID,
    demoSceneName: null,
    projectId: '',
    gridResources: null,
    error: null,
    errorCode: null,

    fetchResources: async () => {
        const { env, gridIdFromQuery, observationId, projectIdFromQuery, reportIdFromQuery } = getEnvStore();

        try {
            let resources: GridResources;
            let demoSceneName: string | null = null;

            if (env === 'demo') {
                const demoResources = await fetchDemoResources(gridIdFromQuery);
                resources = demoResources.resources;
                demoSceneName = demoResources.sceneName;
            } else if (env === 'local-offline') {
                resources = await fetchLocalSampleResources(gridIdFromQuery);
            } else if (observationId) {
                resources = await fetchObservationResources(projectIdFromQuery!, reportIdFromQuery!, observationId);
            } else {
                resources = await fetchGridResources(gridIdFromQuery);
            }

            Sentry.setTag('project.id', resources.project_id);

            await hydratePersistedState(resources.id, resources.project_id, observationId, reportIdFromQuery);

            set({
                status: 'loaded',
                gridId: resources.id as UUID,
                demoSceneName,
                projectId: resources.project_id,
                gridResources: resources,
                error: null,
                errorCode: null,
            });
        } catch (e) {
            const error = e instanceof ResourceError ? e.message : 'Failed to load resources';
            const errorCode = e instanceof ResourceError ? e.statusCode : 500;
            console.error('Error fetching resources:', e);
            set({ status: 'error', error, errorCode });
        }
    },
}));

export const getResourceStore = () => useResourceStore.getState();
